import { createTheme, ThemeOptions } from '@mui/material/styles';

// Heritage Theme - Dignified, Conservative Design for Digital Signage
// Deep Royal Blues, Very Light Off-White, Darker Gold for Accessibility

export const heritageThemeOptions: ThemeOptions = {
  direction: 'rtl',
  palette: {
    primary: {
      main: '#1e3a5f', // Deep Royal Navy Blue
      light: '#2c4a6e',
      dark: '#152a47',
      contrastText: '#ffffff',
    },
    secondary: {
      main: '#9a7209', // Darker Gold for better accessibility
      light: '#b8860b',
      dark: '#7a5a07',
      contrastText: '#ffffff',
    },
    background: {
      default: '#fcfbfa', // Much lighter - almost white
      paper: '#ffffff',
    },
    text: {
      primary: '#1e3a5f', // Navy for main text
      secondary: '#5a6978', // Muted blue-gray
    },
    warning: {
      main: '#9a7209', // Darker Gold
      light: '#b8860b',
      dark: '#7a5a07',
    },
    error: {
      main: '#c53030', // Muted red for emergency
      light: '#e53e3e',
      dark: '#9b2c2c',
    },
    success: {
      main: '#2d5a3d', // Muted green
      light: '#48855c',
      dark: '#1e3d29',
    },
    grey: {
      50: '#fcfbfa',
      100: '#f8f7f5',
      200: '#f0eeec',
      300: '#e0dedb',
      400: '#a8a49c',
      500: '#7a766e',
      600: '#5a5650',
      700: '#3d3a36',
      800: '#252320',
      900: '#171614',
    },
  },
  typography: {
    h1: {
      fontSize: '2.5rem',
      fontWeight: 700,
      letterSpacing: '0.02em',
    },
    h2: {
      fontSize: '2rem',
      fontWeight: 700,
      letterSpacing: '0.02em',
    },
    h3: {
      fontSize: '1.5rem',
      fontWeight: 600,
    },
    h4: {
      fontSize: '1.25rem',
      fontWeight: 600,
    },
    h5: {
      fontSize: '1.1rem',
      fontWeight: 600,
    },
    h6: {
      fontSize: '1rem',
      fontWeight: 600,
    },
    body1: {
      fontSize: '1rem',
      lineHeight: 1.6,
    },
    body2: {
      fontSize: '0.875rem',
      lineHeight: 1.5,
    },
  },
  shape: {
    borderRadius: 12,
  },
  components: {
    MuiPaper: {
      styleOverrides: {
        root: {
          backgroundImage: 'none',
        },
      },
    },
    MuiButton: {
      styleOverrides: {
        root: {
          textTransform: 'none',
          fontWeight: 600,
          borderRadius: 8,
          transition: 'box-shadow 0.25s ease, background-color 0.2s ease',
        },
        containedPrimary: {
          '&:hover': {
            boxShadow: '0 6px 20px rgba(30, 58, 95, 0.35)',
          },
        },
        containedSecondary: {
          '&:hover': {
            boxShadow: '0 6px 20px rgba(154, 114, 9, 0.35)',
          },
        },
      },
    },
    MuiCard: {
      styleOverrides: {
        root: {
          borderRadius: 16,
          boxShadow: '0 4px 24px rgba(30, 58, 95, 0.10), 0 1px 4px rgba(30, 58, 95, 0.06)',
          transition: 'box-shadow 0.25s ease',
        },
      },
    },
    MuiDialog: {
      styleOverrides: {
        paper: {
          borderRadius: 16,
        },
      },
    },
    MuiChip: {
      styleOverrides: {
        root: {
          fontWeight: 500,
        },
      },
    },
  },
};

export const heritageTheme = createTheme(heritageThemeOptions);

// Custom colors for specific use cases
export const heritageColors = {
  // Main palette
  navy: '#1e3a5f',
  navyLight: '#2c4a6e',
  navyDark: '#152a47',
  
  // Gold accents - darkened significantly for accessibility
  gold: '#8a6d08', // Darker gold for text
  goldLight: '#9a7209',
  goldDark: '#6a5306',
  goldBg: '#f5f0e0', // Light gold for backgrounds
  
  // Background colors - much lighter, almost white
  cream: '#fcfbfa',
  creamLight: '#fefefe',
  white: '#ffffff',
  
  // Accent colors (muted, conservative)
  teal: '#2d5a5a',
  emerald: '#2d5a3d',
  slate: '#475569',
  
  // Status colors
  emergency: '#c53030',
  emergencyLight: '#fed7d7',
  
  // Timeline specific colors (muted versions)
  timelinePrayer: '#2c4a6e',
  timelineClass: '#2d5a3d',
  timelineSimple: '#8a6d08',
  
  // Borders and dividers
  border: '#e8e6e1',
  borderLight: '#f0eeec',
};
